import { Activity, Attempt } from '@/common/types/session';
import dayjs from 'dayjs';

// Helper function to create CSV content
function createCSV(headers: string[], rows: string[][]): string {
  let csvContent = 'data:text/csv;charset=utf-8,';
  csvContent += headers.join(',') + '\n';
  rows.forEach((row) => {
    csvContent += row.map((cell) => `"${cell}"`).join(',') + '\n';
  });
  return csvContent;
}

// Helper function to trigger download
function downloadCSV(csvContent: string, filename: string) {
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportMysticMatchToCSV(
  activity: Activity,
  attempts: Attempt[],
) {
  const headers = [
    'Attempt',
    'Correct Answer',
    'Student Choice',
    'Other Cards Presented',
  ];
  const rows = attempts.map((attempt, index) => [
    (index + 1).toString(),
    attempt.correctAnswer,
    attempt.studentAnswer,
    (attempt.incorrectAnswers || []).join('; '),
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `MysticMatch_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

export function exportWordWeaverToCSV(activity: Activity, attempts: Attempt[]) {
  const headers = ['Attempt', 'Served Word', 'Student Attempt'];
  const rows = attempts.map((attempt, index) => [
    (index + 1).toString(),
    attempt.correctAnswer,
    attempt.studentAnswer,
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `WordWeaver_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

export function exportPhonemeForgeToCSV(
  activity: Activity,
  attempts: Attempt[],
) {
  const headers = ['Attempt', 'Served Word', 'Student Attempt'];
  const rows = attempts.map((attempt, index) => [
    (index + 1).toString(),
    attempt.correctAnswer,
    attempt.studentAnswer,
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `PhonemeForge_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

export function exportMorphMasterToCSV(
  activity: Activity,
  attempts: Attempt[],
) {
  const headers = ['Attempt', 'Served Word', 'Student Attempt'];
  const rows = attempts.map((attempt, index) => [
    (index + 1).toString(),
    attempt.correctAnswer,
    attempt.studentAnswer,
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `MorphMaster_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

export function exportSentenceBuilderToCSV(
  activity: Activity,
  attempts: Attempt[],
) {
  const headers = ['Attempt', 'Served Sentence', 'Student Attempt'];
  const rows = attempts.map((attempt: any, index) => [
    (index + 1).toString(),
    attempt.correctAnswer.sentence,
    attempt.studentAnswer,
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `SentenceBuilder_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

export function exportTrickyWordsToCSV(
  activity: Activity,
  attempts: Attempt[],
) {
  const headers = [
    'Attempt',
    'Correct Answer',
    'Student Choice',
    'Other Options Presented',
  ];
  const rows = attempts.map((attempt, index) => [
    (index + 1).toString(),
    attempt.correctAnswer,
    attempt.studentAnswer,
    (attempt.incorrectChoices || []).join('; '),
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `TrickyWords_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

export function exportWordRiftsToCSV(activity: Activity, attempts: Attempt[]) {
  const headers = [
    'Attempt',
    'Sentence',
    'Correct Choice',
    'Student Choice',
    'Cards Presented',
  ];
  const rows = attempts.map((attempt, index) => [
    (index + 1).toString(),
    attempt.context || '',
    attempt.correctAnswer,
    attempt.studentAnswer,
    (attempt.incorrectAnswers || []).join('; '),
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `WordRifts_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

export function exportWordSumsToCSV(activity: Activity, attempts: Attempt[]) {
  const headers = ['Attempt', 'Served Word', 'Student Attempt'];
  const rows = attempts.map((attempt, index) => [
    (index + 1).toString(),
    attempt.correctAnswer,
    attempt.studentAnswer,
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `WordSums_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

export function exportAssessmentToCSV(activity: Activity, attempts: Attempt[]) {
  const headers = ['Attempt', 'Served Word', 'Student Attempt'];
  const rows = attempts.map((attempt, index) => [
    (index + 1).toString(),
    attempt.correctAnswer,
    attempt.studentAnswer,
  ]);

  const csvContent = createCSV(headers, rows);
  const filename = `Assessment_${dayjs(activity.startedAt).format(
    'YYYY-MM-DD_HH-mm',
  )}.csv`;
  downloadCSV(csvContent, filename);
}

// Export function map
export const exportFunctions: Record<
  string,
  (activity: Activity, attempts: Attempt[]) => void
> = {
  mysticMatch: exportMysticMatchToCSV,
  wordWeaver: exportWordWeaverToCSV,
  phonemeForge: exportPhonemeForgeToCSV,
  sentenceBuilder: exportSentenceBuilderToCSV,
  trickyWords: exportTrickyWordsToCSV,
  wordRifts: exportWordRiftsToCSV,
  wordSums: exportWordSumsToCSV,
  assessment: exportAssessmentToCSV,
  morphMaster: exportMorphMasterToCSV,
};

// Main export function
export function exportToCSV(activity: Activity, attempts: Attempt[]) {
  const exportFunction = exportFunctions[activity.activityName];
  if (exportFunction) {
    exportFunction(activity, attempts);
  } else {
    console.error(
      `No export function found for activity: ${activity.activityName}`,
    );
  }
}

export default exportToCSV;
